'use client';

import React from 'react';

import { LikeIcon } from '@depop/web-ui-kit/Icons/LikeIcon';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { NavigationLink } from '../NavigationLink';
import { NavigationListItem } from '../NavigationListItem';

import { ActivityTrackerEventType } from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config';
import { useExperiments } from '@/modules/experiments/useExperiments';
import { getLoginRedirectParams } from '@/modules/login/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function NavigationLikesButton() {
  const t = useTranslations('common');
  const [isAuthenticated, currentUser] = useCurrentUser();
  const pathname = usePathname();
  const username = isAuthenticated ? currentUser?.username : '[username]';
  const isActive = pathname?.endsWith(`/${username}/likes/`);
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });
  const displayBreakpoint = !isAuthenticated ? 'smUp' : undefined;

  const likesPath = `/${username}/likes/`;

  const linkProps = {
    href: `${likesPath}?isMine=true`,
  };

  const { web_9275_guest_likes } = useExperiments(['web_9275_guest_likes']);

  function handleLikesClick() {
    sendActivityTrackerEvent(ActivityTrackerEventType.NAVBAR_LIKES_ACTION);
  }

  if (web_9275_guest_likes === VARIANT_IDENTIFIER && !isAuthenticated) {
    const guestLikesUrl = '/likes/';
    return (
      <NavigationListItem handleNavigationListItemClick={handleLikesClick}>
        <NavigationLink
          href={guestLikesUrl}
          isActive={pathname === guestLikesUrl}
          aria-label={t('Nav.Likes')}
        >
          <LikeIcon size={28} aria-hidden="true" />
        </NavigationLink>
      </NavigationListItem>
    );
  }

  if (!isAuthenticated) {
    const loginUrl = `/login/${getLoginRedirectParams(likesPath)}`;

    return (
      <NavigationListItem
        handleNavigationListItemClick={handleLikesClick}
        displayBreakpoint={displayBreakpoint}
      >
        <NavigationLink
          href={loginUrl}
          as={loginUrl}
          aria-label={t('Nav.Likes')}
        >
          <LikeIcon size={28} aria-hidden="true" />
        </NavigationLink>
      </NavigationListItem>
    );
  }

  return (
    <NavigationListItem
      handleNavigationListItemClick={handleLikesClick}
      displayBreakpoint={displayBreakpoint}
    >
      <NavigationLink
        isActive={isActive}
        {...linkProps}
        aria-label={t('Nav.Likes')}
      >
        <LikeIcon size={28} aria-hidden="true" />
      </NavigationLink>
    </NavigationListItem>
  );
}
